import React from 'react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const Contact: React.FC = () => {
  // SEO optimization for Contact page
  useDocumentTitle({
    title: 'Contact Us - WageWise | H1B Visa Job Search Support',
    description: 'Get in touch with WageWise for support with H1B visa job search, sponsorship questions, or technical assistance. We\'re here to help international professionals.',
    keywords: 'contact WageWise, H1B visa support, job search help, customer service, H1B sponsorship questions',
    canonicalUrl: 'https://wagewise.app/contact',
    noIndex: false
  });

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <header className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Contact Us</h1>
        <p className="text-gray-600">Get in touch with our team for support with H1B visa job search</p>
      </header>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Contact Information */}
        <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
          <h2 className="text-2xl font-semibold mb-6 text-gray-800">Get In Touch</h2>
          
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-1">Email Support</h3>
                <p className="text-gray-600 mb-2">For general inquiries and support</p>
                <a href="mailto:support@wagewise.app" className="text-blue-600 hover:text-blue-800 font-medium">
                  support@wagewise.app
                </a>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="bg-green-100 p-3 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-1">H1B Sponsorship Questions</h3>
                <p className="text-gray-600 mb-2">Questions about H1B data or sponsorship</p>
                <a href="mailto:h1b@wagewise.app" className="text-blue-600 hover:text-blue-800 font-medium">
                  h1b@wagewise.app
                </a>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="bg-purple-100 p-3 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-1">Technical Support</h3>
                <p className="text-gray-600 mb-2">Website issues or technical problems</p>
                <a href="mailto:tech@wagewise.app" className="text-blue-600 hover:text-blue-800 font-medium">
                  tech@wagewise.app
                </a>
              </div>
            </div>
          </div>

          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-2">Response Time</h3>
            <p className="text-sm text-gray-600">
              We typically respond to all inquiries within 24-48 hours during business days. 
              For urgent technical issues, please include "URGENT" in your subject line.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
          <h2 className="text-2xl font-semibold mb-6 text-gray-800">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">How current is the H1B sponsorship data?</h3>
              <p className="text-gray-600 text-sm">
                Our data is based on official government records and is updated regularly. However, 
                company sponsorship policies can change, so we recommend verifying current status 
                directly with employers.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Is WageWise free to use?</h3>
              <p className="text-gray-600 text-sm">
                Yes, WageWise is completely free to use. We believe everyone should have access 
                to H1B sponsorship information to make informed career decisions.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Can you guarantee H1B sponsorship?</h3>
              <p className="text-gray-600 text-sm">
                No, we cannot guarantee sponsorship. We provide historical data to help you 
                identify companies that have sponsored H1B visas in the past. Sponsorship 
                decisions are made by individual employers.
              </p>
            </div>

           
          </div>
        </div>
      </div>

      {/* Additional Resources */}
      <div className="mt-12 bg-blue-50 rounded-lg p-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">Additional Resources</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">H1B Visa Information</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• <a href="https://www.uscis.gov/working-in-the-united-states/h-1b-specialty-occupations" 
                     target="_blank" rel="noopener noreferrer" 
                     className="text-blue-600 hover:text-blue-800">
                     Official USCIS H1B Information
                   </a></li>
              <li>• <a href="https://www.dol.gov/agencies/eta/foreign-labor" 
                     target="_blank" rel="noopener noreferrer" 
                     className="text-blue-600 hover:text-blue-800">
                     Department of Labor Foreign Labor Certification
                   </a></li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">Job Search Tips</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Research company sponsorship history</li>
              <li>• Network with current employees</li>
              <li>• Highlight your unique skills and qualifications</li>
              <li>• Be transparent about visa requirements early</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact; 